import CollapseCategories16 from '@carbon/icons-react/es/collapse-categories/16';
import ExpandCategories16 from '@carbon/icons-react/es/expand-categories/16';
import Timer16 from '@carbon/icons-react/es/timer/16';
import Timer20 from '@carbon/icons-react/es/timer/20';
import { Radio, RadioChangeEvent, Tooltip } from 'antd';
import { Moment } from 'moment';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import {
  TimesheetApprovalType,
  Configuration,
} from 'api/timesheets/configuration/configuration';
import { ReactComponent as ApproveOutline16 } from 'assets/svgs/approve-outline.svg';
import { Button } from 'components/atoms/button';
import { SearchInput } from 'components/atoms/input';
import { PageLoading } from 'components/atoms/loading';
import {
  CompactMenu,
  CompactMenuTitle,
} from 'components/atoms/menu/compact-menu';
import { RadioGroup } from 'components/atoms/radio-buttons';
import { TableSkeleton } from 'components/atoms/table';
import { HeaderWithIconTitle } from 'components/molecules/page/page';
import { TimesheetApprovalAllUserView } from 'components/organisms/timesheets/approval-all-user-view/all-user-view';
import { ApprovalLoadingState } from 'components/organisms/timesheets/common/approval-loading-state';
import { TimesheetFilter } from 'components/organisms/timesheets/common/filter';
import { useGrid } from 'components/organisms/timesheets/common/hooks';
import { ReturnToCurrentWeek } from 'components/organisms/timesheets/common/return-to-current-week';
import { WeekRangePicker } from 'components/organisms/timesheets/date-pickers';
import { Timesheet, WeekPreset } from 'components/organisms/timesheets/types';
import { Argon } from 'components/templates';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { useTimesheetApprovals } from './use-timesheet-approvals';
import { useTimesheetFilter } from './use-timesheet-filter';
import { useTimesheetUrlState } from './use-timesheet-url-state';

const StyledRadioGroup = styled(RadioGroup)`
  padding: 0 var(--spacing-03);
  grid-template-columns: repeat(1, minmax(0, 1fr));
  .ant-radio-button-wrapper {
    svg {
      margin-right: 0;
    }
  }
`;

const SearchInputStyled = styled(SearchInput)`
  ${Button} {
    border: none;
  }
`;

const ValidWeekPresets = [WeekPreset.ThisWeek, WeekPreset.PreviousWeek];

const WeekPresetsAllowedInURL = [WeekPreset.ThisWeek];

export type ToggleState = 'expand' | 'collapse' | null;

interface Props {
  configuration: Configuration;
  startDate: Moment;
  onStartDateChange: (date: Moment) => void;
  timesheets: Timesheet[];
  onTimesheetStateChange: (
    userId: number,
    payload: Partial<Omit<Timesheet, 'userId'>>,
  ) => void;
  refreshData: () => void;
  pendingApprovalCount: number;
  isTimesheetLoading: boolean;
}
export const TimesheetApprovalAllUserPage: FC<Props> = ({
  configuration,
  startDate,
  onStartDateChange,
  timesheets,
  onTimesheetStateChange,
  refreshData,
  pendingApprovalCount,
  isTimesheetLoading,
}) => {
  const translate = useTranslate();
  const { push } = useHistory();
  const [mode, setMode] = useState<ToggleState>('expand');

  const searchPlaceholderText = useTranslation(
    'timesheets.approval.search_placeholder',
  );
  const searchPlaceholderTextPO = useTranslation(
    'timesheets.approval.po.search_placeholder',
  );

  const { onGridReady, gridApi } = useGrid();

  const { startDate: _startDate, setStartDate: _setStartDate } =
    useTimesheetUrlState({
      allowedWeekPresetsInUrl: WeekPresetsAllowedInURL,
      validWeekPresets: ValidWeekPresets,
      defaultStartDate: startDate,
      url: '/timesheets/:week/approvals',
    });

  useEffect(() => {
    if (!_startDate.isSame(startDate, 'day')) {
      onStartDateChange(_startDate);
    }
  }, [_startDate, onStartDateChange, startDate]);

  const { userTimesheets, approveTimesheet, sendReminder, isLoading } =
    useTimesheetApprovals(startDate, timesheets, onTimesheetStateChange);

  const {
    roleOptions,
    viewOptions,
    selectedViewType,
    setSelectedViewType,
    selectedRole,
    setSelectedRole,
    filteredTimesheets,
    searchValue,
    setSearchValue,
  } = useTimesheetFilter(userTimesheets, configuration.approvalSetting);

  const onChange = (e) => setSearchValue(e.target.value);

  const isManagerApproval = useMemo(
    () => configuration.approvalSetting === TimesheetApprovalType.Manager,
    [configuration.approvalSetting],
  );

  const expandAllTooltipText = translate('expand_all_projects');
  const collapseAllTooltipText = translate('collapse_all_projects');

  const onCollapseChange = (e: RadioChangeEvent) => {
    const toggleMode = e.target.value as ToggleState;

    switch (toggleMode) {
      case 'collapse':
        gridApi?.collapseAll();
        setMode(toggleMode);
        break;
      case 'expand':
        gridApi?.expandAll();
        setMode(toggleMode);
        break;
      default:
    }
  };

  useEffect(() => {
    setMode('expand');
  }, []);

  return configuration == null ? (
    <PageLoading />
  ) : (
    <Argon
      title={
        <HeaderWithIconTitle
          icon={<Timer20 />}
          title={<FormattedMessage id="menu.label.timesheets" />}
        />
      }
      tabs={
        <CompactMenu
          mode="horizontal"
          selectedKeys={['approvals']}
          onClick={({ key }) => {
            if (key === 'my-timesheet') {
              push('/timesheets/this-week/my-timesheet');
            }
          }}
        >
          <CompactMenu.Item key="my-timesheet">
            <CompactMenuTitle
              icon={<Timer16 />}
              text={<FormattedMessage id="timesheets.tabs.my_timesheets" />}
            />
          </CompactMenu.Item>
          <CompactMenu.Item key="approvals">
            <CompactMenuTitle
              icon={<ApproveOutline16 />}
              text={
                <FormattedMessage
                  id="timesheets.tabs.approvals"
                  values={{ count: pendingApprovalCount }}
                />
              }
            />
          </CompactMenu.Item>
        </CompactMenu>
      }
      actions={{
        primary: (
          <>
            <WeekRangePicker value={_startDate} onChange={_setStartDate} />
            <ReturnToCurrentWeek
              currentDate={_startDate}
              onResetToDefaultWeek={_setStartDate}
            />
            {!isManagerApproval && (
              <StyledRadioGroup value={mode} onChange={onCollapseChange}>
                {mode === 'expand' ? (
                  <Radio.Button value="collapse">
                    <Tooltip title={collapseAllTooltipText}>
                      <CollapseCategories16 />
                    </Tooltip>
                  </Radio.Button>
                ) : (
                  <Radio.Button value="expand">
                    <Tooltip title={expandAllTooltipText}>
                      <ExpandCategories16 />
                    </Tooltip>
                  </Radio.Button>
                )}
              </StyledRadioGroup>
            )}
          </>
        ),
        secondary: (
          <>
            <SearchInputStyled
              disabled={isTimesheetLoading}
              placeholder={
                configuration.approvalSetting === TimesheetApprovalType.Manager
                  ? searchPlaceholderText
                  : searchPlaceholderTextPO
              }
              size="small"
              onChange={onChange}
              value={searchValue}
              direction="right"
            />
            <TimesheetFilter
              options={viewOptions}
              labelPrefix={translate('timesheets.view_types.title')}
              selected={selectedViewType}
              onChange={setSelectedViewType}
              isManagerApproval={isManagerApproval}
            />
            {isManagerApproval && (
              <TimesheetFilter
                options={roleOptions}
                labelPrefix={translate('timesheets.show_types.title')}
                selected={selectedRole}
                onChange={setSelectedRole}
                isManagerApproval={isManagerApproval}
              />
            )}
          </>
        ),
      }}
      content={
        <>
          <Helmet>
            <title>{translate('timesheets.title.all_users_approval')}</title>
          </Helmet>
          {isTimesheetLoading ? (
            isManagerApproval ? (
              <ApprovalLoadingState />
            ) : (
              <TableSkeleton columnCount={8} />
            )
          ) : (
            <TimesheetApprovalAllUserView
              startDate={_startDate}
              configuration={configuration}
              userTimesheets={filteredTimesheets}
              approveTimesheet={approveTimesheet}
              sendReminder={sendReminder}
              refreshData={refreshData}
              searchValue={searchValue}
              isLoading={isLoading}
              isManagerApproval={isManagerApproval}
              onGridReady={onGridReady}
              gridApi={gridApi}
            />
          )}
        </>
      }
    />
  );
};
